import React from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarCheck, ChevronsDownUp, ChevronsUpDown, Crosshair, History, Keyboard, LoaderCircle, Minus, Plus, Printer, RefreshCw, Search, PanelRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Toggle } from '@/components/ui/toggle'
import { Separator } from '@/components/ui/separator'
import { ZOOM_LEVELS, type ZoomLevel } from '../lib/constants'
import { useGanttStore } from '../store/ganttStore'

interface Props {
  hasBaseline: boolean
  readOnly: boolean
  onToday: () => void
  onCollapseAll: () => void
  onSearchNext: () => void
  onRefresh: () => void
  refreshing: boolean
  onOpenLog: () => void
  onOpenShortcuts: () => void
  /** Task 8: opens the print/PDF dialog. Undefined when the viewer has no EXPORT right — the
   * button is then not rendered at all. Stays available on an archived schedule: printing is
   * not an edit. */
  onOpenPrint?: () => void
  editingSlot?: React.ReactNode
}

const GanttToolbar: React.FC<Props> = ({ hasBaseline, readOnly, onToday, onCollapseAll, onSearchNext, onRefresh, refreshing, onOpenLog, onOpenShortcuts, onOpenPrint, editingSlot }) => {
  const { t } = useTranslation()
  const zoom = useGanttStore((s) => s.zoom)
  const setZoom = useGanttStore((s) => s.setZoom)
  const zoomStep = useGanttStore((s) => s.zoomStep)
  const expandAll = useGanttStore((s) => s.expandAll)
  const search = useGanttStore((s) => s.search)
  const setSearch = useGanttStore((s) => s.setSearch)
  const criticalOnly = useGanttStore((s) => s.criticalOnly)
  const setCriticalOnly = useGanttStore((s) => s.setCriticalOnly)
  const showCriticalPath = useGanttStore((s) => s.showCriticalPath)
  const setShowCriticalPath = useGanttStore((s) => s.setShowCriticalPath)
  const showBaseline = useGanttStore((s) => s.showBaseline)
  const setShowBaseline = useGanttStore((s) => s.setShowBaseline)
  const panelOpen = useGanttStore((s) => s.panelOpen)
  const setPanelOpen = useGanttStore((s) => s.setPanelOpen)
  const pending = useGanttStore((s) => s.pendingCount)

  return (
    <div className="py-2 flex items-center gap-2 flex-wrap">
      {!readOnly && editingSlot && (<>{editingSlot}<Separator orientation="vertical" className="h-6" /></>)}
      <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => zoomStep(-1)} disabled={zoom === 'project'} aria-label={t('Schedule_zoom_out')}><Minus className="h-4 w-4" /></Button>
      <Select value={zoom} onValueChange={(v) => setZoom(v as ZoomLevel)}>
        <SelectTrigger className="h-8 w-[140px] text-sm"><SelectValue /></SelectTrigger>
        <SelectContent>{ZOOM_LEVELS.map((z) => <SelectItem key={z} value={z}>{t(`Schedule_zoom_${z}`)}</SelectItem>)}</SelectContent>
      </Select>
      <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => zoomStep(1)} disabled={zoom === 'day'} aria-label={t('Schedule_zoom_in')}><Plus className="h-4 w-4" /></Button>
      <Button variant="outline" size="sm" className="h-8" onClick={onToday}><Crosshair className="h-4 w-4" />{t('Schedule_today')}</Button>
      <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={expandAll} aria-label={t('Schedule_expand_all')}><ChevronsUpDown className="h-4 w-4" /></Button>
      <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={onCollapseAll} aria-label={t('Schedule_collapse_all')}><ChevronsDownUp className="h-4 w-4" /></Button>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder={t('Schedule_search_activities')} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') onSearchNext() }} className="h-8 w-[200px] pl-8 text-sm" />
      </div>
      <Toggle size="sm" pressed={criticalOnly} onPressedChange={setCriticalOnly} className="h-8">{t('Schedule_critical_only')}</Toggle>
      <div className="ml-auto flex items-center gap-2">
        {pending > 0 && <span className="flex items-center gap-1 text-xs text-muted-foreground"><LoaderCircle className="h-3 w-3 animate-spin" />{t('Schedule_recalculating')}</span>}
        <Toggle size="sm" pressed={showCriticalPath} onPressedChange={setShowCriticalPath} className="h-8">{t('Schedule_critical_path')}</Toggle>
        <Toggle size="sm" pressed={showBaseline && hasBaseline} onPressedChange={setShowBaseline} disabled={!hasBaseline} className="h-8"><CalendarCheck className="h-4 w-4 mr-1" />{t('Schedule_baseline')}</Toggle>
        {onOpenPrint && <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={onOpenPrint} aria-label={t('Schedule_print')} title={t('Schedule_print')}><Printer className="h-4 w-4" /></Button>}
        <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={onOpenShortcuts} aria-label={t('Schedule_shortcuts')}><Keyboard className="h-4 w-4" /></Button>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={onOpenLog} aria-label={t('Schedule_activity_log')}><History className="h-4 w-4" /></Button>
        <Toggle size="sm" pressed={panelOpen} onPressedChange={setPanelOpen} className="h-8 w-8 p-0" aria-label={t('Schedule_panel')}><PanelRight className="h-4 w-4" /></Toggle>
        <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={onRefresh} disabled={refreshing} aria-label={t('Common_refresh')}><RefreshCw className={refreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} /></Button>
      </div>
    </div>
  )
}
export default GanttToolbar
